import React, { useEffect, useMemo, useRef, useState } from "react";
import Navbar from "../components/Navbar";
import "../styles/Dashboard.css";
import { deleteData, getData, postData, putData } from "../services/Request";
import { toast } from "react-toastify";
import { useLoader } from "../context/LoaderContext";
import Sidebar from "../components/Sidebar";
import { PROVINCE_OPTIONS } from "../constants/provinceName";
import Select from "react-select";

const provinceOptions = PROVINCE_OPTIONS.map((p) => ({
  value: p,
  label: p,
}));

const emptyForm = {
  seller_name: "",
  cnic: "",
  business_name: "",
  province: "",
  address: "",
};

const hasValue = (value) => `${value ?? ""}`.trim().length > 0;

const getSellerId = (seller) => seller?.id ?? seller?._id ?? "";

const normaliseList = (payload) => {
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload?.data)) return payload.data;
  if (Array.isArray(payload?.data?.rows)) return payload.data.rows;
  if (Array.isArray(payload?.sellers)) return payload.sellers;
  if (Array.isArray(payload?.items)) return payload.items;
  return [];
};

export default function Sellers() {
  const { showLoader, hideLoader } = useLoader();
  const [sellers, setSellers] = useState([]);
  const [formData, setFormData] = useState(emptyForm);
  const [editingId, setEditingId] = useState(null);
  // const [confirmDeleteId, setConfirmDeleteId] = useState(null);
  const [listLoading, setListLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [page, setPage] = useState(1);
  const [pageSize] = useState(10);
  const [totalPages, setTotalPages] = useState(1);
  const [search, setSearch] = useState("");
  const hasFetchedInitial = useRef(false);
  const startIndex = useMemo(() => (page - 1) * pageSize, [page, pageSize]);
  const isFormComplete =
    hasValue(formData.seller_name) &&
    hasValue(formData.business_name) &&
    hasValue(formData.province) &&
    hasValue(formData.address) &&
    hasValue(formData.cnic);

  const fetchSellers = async ({ withLoader = false } = {}) => {
    try {
      if (withLoader) {
        showLoader();
      } else {
        setListLoading(true);
      }
      const response = await getData(
        `api/seller?page=${page}&pageSize=${pageSize}&search=${encodeURIComponent(
          search,
        )}`,
      );
      setSellers(normaliseList(response));
      const pagination =
        response?.pagination || response?.data?.pagination || {};
      setTotalPages(pagination?.totalPages || 1);
    } catch (error) {
      toast.error("Sellers not loaded try again ");
      console.error("Failed to fetch sellers", error);
    } finally {
      if (withLoader) {
        hideLoader();
      } else {
        setListLoading(false);
      }
    }
  };

  useEffect(() => {
    if (hasFetchedInitial.current) return;
    hasFetchedInitial.current = true;
    fetchSellers({ withLoader: true });
  }, []);

  useEffect(() => {
    fetchSellers();
  }, [page, search]);

  const handleInputChange = (event) => {
    const { name, value } = event.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const resetForm = () => {
    setFormData(emptyForm);
    setEditingId(null);
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!hasValue(formData.cnic)) {
      toast.error("CNIC / NTN is required.");
      return;
    }
    setSubmitting(true);
    try {
      if (editingId) {
        await putData(`api/seller/${editingId}`, formData);
        toast.success("Upadate Seller");
      } else {
        await postData("api/seller", formData);
        toast.success("Add New Seller.");
      }
      resetForm();
      await fetchSellers();
    } catch (error) {
      const action = editingId ? "update" : "add";
      toast.error(`Seller not ${action}.`);
      console.error("Submit seller failed", error);
    } finally {
      setSubmitting(false);
    }
  };

  const loadSellerForEdit = async (sellerId) => {
    if (!sellerId) {
      toast.error("Seller ID not found.");
      return;
    }
    try {
      showLoader();
      const response = await getData(`api/seller/${sellerId}`);
      const payload =
        response?.data && !Array.isArray(response.data)
          ? response.data
          : response;
      setFormData({
        ...emptyForm,
        ...payload,
        cnic: payload?.cnic ?? "",
      });
      setEditingId(sellerId);
    } catch (error) {
      toast.error("Seller details not get");
      console.error("Failed to fetch single seller", error);
    } finally {
      hideLoader();
    }
  };

  // const handleDelete = async (sellerId) => {
  //   const targetId = sellerId || confirmDeleteId;
  //   if (!targetId) {
  //     toast.error("Seller ID nahi mil saka.");
  //     return;
  //   }
  //   const toastId = toast.loading("Deleting seller...");
  //   try {
  //     showLoader();
  //     await deleteData(`api/seller/${targetId}`);
  //     toast.update(toastId, {
  //       render: "Seller deleted.",
  //       type: "success",
  //       isLoading: false,
  //       autoClose: 1500,
  //     });
  //     if (editingId === targetId) {
  //       resetForm();
  //     }
  //     await fetchSellers();
  //   } catch (error) {
  //     toast.update(toastId, {
  //       render: "Seller could not be deleted. Please try again.",
  //       type: "error",
  //       isLoading: false,
  //       autoClose: 2000,
  //     });
  //     console.error("Failed to delete seller", error);
  //   } finally {
  //     hideLoader();
  //     setConfirmDeleteId(null);
  //   }
  // };

  // const openDeleteModal = (sellerId) => setConfirmDeleteId(sellerId);
  // const closeDeleteModal = () => setConfirmDeleteId(null);

  const totalSellers = useMemo(() => sellers.length, [sellers]);
  const uniqueProvinces = useMemo(() => {
    const provinces = sellers.map((seller) => seller?.province).filter(Boolean);
    return [...new Set(provinces)].length;
  }, [sellers]);

  return (
    <div className="app-shell">
      <Sidebar />
      <div className="app-content">
        <Navbar />
        <div className="dashboard-page">
          <main className="dashboard">
            <section className="sample-page seller-page">
              <div className="sample-page__header">
                <h1>Sellers</h1>
              </div>

              <div className="seller-management">
                <div className="seller-card seller-form-card">
                  <div className="seller-card__header">
                    <h3>
                      {editingId ? "Update Branch Details" : "Add New Branch"}
                    </h3>
                    {editingId && (
                      <button
                        type="button"
                        className="text-link-btn"
                        onClick={resetForm}
                      >
                        Cancel edit
                      </button>
                    )}
                  </div>
                  <form className="seller-form" onSubmit={handleSubmit}>
                    <div className="form-grid">
                      <label className="form-field">
                        <span>
                          Branch Name<span className="requied">*</span>
                        </span>
                        <input
                          type="text"
                          name="seller_name"
                          value={formData.seller_name}
                          onChange={handleInputChange}
                          required
                          placeholder="Enter seller name"
                        />
                      </label>

                      <label className="form-field">
                        <span>
                          NTN <span className="requied">*</span>
                        </span>
                        <input
                          type="text"
                          name="cnic"
                          value={formData.cnic}
                          onChange={handleInputChange}
                          required
                          placeholder="Enter NTN"
                        />
                      </label>

                      <label className="form-field">
                        <span>
                          Business Name<span className="requied">*</span>
                        </span>
                        <input
                          type="text"
                          name="business_name"
                          value={formData.business_name}
                          onChange={handleInputChange}
                          placeholder="Store / company title"
                          required
                        />
                      </label>

                      <label className="form-field">
                        <span>
                          Province<span className="requied">*</span>
                        </span>

                        <Select
                          options={provinceOptions}
                          placeholder="Select Province"
                          isSearchable={true}
                          value={provinceOptions.find(
                            (opt) => opt.value === formData.province,
                          )}
                          onChange={(selectedOption) =>
                            setFormData({
                              ...formData,
                              province: selectedOption.value,
                            })
                          }
                        />
                      </label>
                    </div>

                    <label className="form-field">
                      <span>
                        Address <span className="requied">*</span>
                      </span>
                      <textarea
                        name="address"
                        rows="3"
                        value={formData.address}
                        onChange={handleInputChange}
                        required
                        placeholder="Shop address"
                      />
                    </label>

                    <button
                      type="submit"
                      className="primary-btn"
                      disabled={submitting || !isFormComplete}
                    >
                      {submitting
                        ? "Please wait..."
                        : editingId
                          ? "Update seller"
                          : "Add seller"}
                    </button>
                  </form>
                </div>

                <div className="seller-card seller-table-card">
                  <div className="seller-card__header">
                    <h3>Seller Branch list</h3>
                    <button
                      type="button"
                      className="text-link-btn"
                      onClick={() => fetchSellers()}
                      disabled={listLoading}
                    >
                      {listLoading ? "Refreshing..." : "Refresh"}
                    </button>
                  </div>
                  <div
                    className="list-actions"
                    style={{ marginBottom: "10px" }}
                  >
                    <input
                      type="search"
                      placeholder="Search sellers"
                      value={search}
                      onChange={(e) => {
                        setPage(1);
                        setSearch(e.target.value);
                      }}
                    />
                  </div>
                  <div className="seller-table-wrapper">
                    <table className="seller-table">
                      <thead>
                        <tr>
                          <th>No.</th>
                          <th>Name</th>
                          <th>NTN</th>
                          <th>Business</th>
                          <th>Province</th>
                          <th>Address</th>
                          <th>Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {sellers.length === 0 && (
                          <tr>
                            <td colSpan="9" className="empty-state">
                              {listLoading
                                ? "Loading Seller Branches..."
                                : "Branch not loaded."}
                            </td>
                          </tr>
                        )}
                        {sellers.map((seller, index) => {
                          const sellerId = getSellerId(seller);
                          const rowKey =
                            sellerId ?? `${seller?.cnic ?? "row"}-${index}`;
                          return (
                            <tr key={rowKey}>
                              <td>{startIndex + index + 1}</td>
                              <td>{seller?.seller_name || "-"}</td>
                              <td>{seller?.cnic || "-"}</td>
                              <td>{seller?.business_name || "-"}</td>
                              <td>{seller?.province || "-"}</td>
                              <td>
                                {seller?.address || (
                                  <span className="muted-text">N/A</span>
                                )}
                              </td>

                              <td>
                                <div className="table-actions">
                                  <button
                                    type="button"
                                    onClick={() => loadSellerForEdit(sellerId)}
                                    disabled={!sellerId}
                                  >
                                    Edit
                                  </button>
                                  {/* <button
                                    type="button"
                                    className="danger"
                                    onClick={() => openDeleteModal(sellerId)}
                                    disabled={!sellerId}
                                  >
                                    Delete
                                  </button> */}
                                </div>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                  <div className="table-pagination">
                    <button
                      type="button"
                      disabled={page <= 1 || listLoading}
                      onClick={() => setPage((p) => Math.max(1, p - 1))}
                    >
                      Prev
                    </button>
                    <span>
                      Page {page} of {totalPages}
                    </span>
                    <button
                      type="button"
                      disabled={page >= totalPages || listLoading}
                      onClick={() => setPage((p) => p + 1)}
                    >
                      Next
                    </button>
                  </div>
                  {/* {confirmDeleteId && (
                    <div className="modal-backdrop">
                      <div className="modal-card">
                        <h3>Delete seller</h3>
                        <p>This action cannot be undone. Delete this seller?</p>
                        <div className="modal-actions">
                          <button
                            type="button"
                            className="outline-btn"
                            onClick={closeDeleteModal}
                          >
                            Cancel
                          </button>
                          <button
                            type="button"
                            className="primary-btn danger"
                            onClick={() => handleDelete(confirmDeleteId)}
                          >
                            Delete
                          </button>
                        </div>
                      </div>
                    </div>
                  )} */}
                </div>
              </div>
            </section>
          </main>
        </div>
      </div>
    </div>
  );
}
